import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, X, Trash2, Clock, Users, CalendarDays, Repeat } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { getBackendUrl } from '../utils/getBackendUrl';
import { todayET, etInputToUTC } from '../utils/timezone';

const API = `${getBackendUrl()}/api`;

const QUICK_OPTIONS = [
  { label: 'In 1 hour', hours: 1 },
  { label: 'In 4 hours', hours: 4 },
  { label: 'Tomorrow 9 AM', preset: 'tomorrow9' },
  { label: 'In 1 week', days: 7 },
  { label: 'In 2 weeks', days: 14 },
  { label: 'In 1 month', days: 30 },
];

export default function NoteReminderDialog({ noteId, projectId, open, onClose }) {
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [reminderDate, setReminderDate] = useState('');
  const [reminderTime, setReminderTime] = useState('09:00');
  const [recurringWeekly, setRecurringWeekly] = useState(false);
  const [existingReminders, setExistingReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    if (open) {
      fetchTeamMembers();
      fetchExistingReminders();
      // Default to tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setReminderDate(tomorrow.toISOString().split('T')[0]);
      setReminderTime('09:00');
      setSelectedMembers([]);
      setRecurringWeekly(false);
      setSelectAll(false);
    }
  }, [open, noteId]);

  const fetchTeamMembers = async () => {
    try {
      const res = await axios.get(`${API}/team-members`);
      setTeamMembers(res.data.filter(m => m.role === 'admin' || m.role === 'employee'));
    } catch (err) {
      console.error('Failed to fetch team members');
    }
  };

  const fetchExistingReminders = async () => {
    try {
      const res = await axios.get(`${API}/note-reminders/${noteId}`);
      setExistingReminders(res.data);
    } catch (err) {
      console.error('Failed to fetch reminders');
    }
  };

  const handleQuickOption = (opt) => {
    const now = new Date();
    let target;
    if (opt.preset === 'tomorrow9') {
      target = new Date(now);
      target.setDate(target.getDate() + 1);
      target.setHours(9, 0, 0, 0);
      setReminderDate(target.toISOString().split('T')[0]);
      setReminderTime('09:00');
      return;
    }
    if (opt.hours) {
      target = new Date(now.getTime() + opt.hours * 3600000);
    } else if (opt.days) {
      target = new Date(now.getTime() + opt.days * 86400000);
      target.setHours(9, 0, 0, 0);
    }
    setReminderDate(target.toISOString().split('T')[0]);
    setReminderTime(target.toTimeString().slice(0, 5));
  };

  const toggleMember = (id) => {
    setSelectedMembers(prev =>
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedMembers(teamMembers.map(m => m.id));
    } else {
      setSelectedMembers([]);
    }
  };

  const handleSubmit = async () => {
    if (selectedMembers.length === 0) {
      toast.error('Select at least one person to remind');
      return;
    }
    if (!reminderDate) {
      toast.error('Please set a reminder date');
      return;
    }

    setLoading(true);
    try {
      const remindAtUTC = etInputToUTC(`${reminderDate}T${reminderTime}`);
      await axios.post(`${API}/note-reminders`, {
        note_id: noteId,
        project_id: projectId,
        recipient_ids: selectedMembers,
        remind_at: remindAtUTC,
        recurring_weekly: recurringWeekly
      });
      toast.success(recurringWeekly ? 'Weekly reminder set!' : 'Reminder set!');
      onClose();
    } catch (err) {
      toast.error('Failed to set reminder');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReminder = async (id) => {
    try {
      await axios.delete(`${API}/note-reminders/${id}`);
      setExistingReminders(prev => prev.filter(r => r.id !== id));
      toast.success('Reminder deleted');
    } catch (err) {
      toast.error('Failed to delete reminder');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-white border-slate-200 text-slate-900 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <Bell className="w-5 h-5 text-orange-500" />
            Set Note Reminder
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Quick Options */}
          <div>
            <Label className="text-slate-600 text-sm flex items-center gap-1 mb-2">
              <Clock className="w-3.5 h-3.5" /> Quick Set
            </Label>
            <div className="flex flex-wrap gap-2">
              {QUICK_OPTIONS.map((opt) => (
                <button
                  key={opt.label}
                  onClick={() => handleQuickOption(opt)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 hover:text-slate-900 border border-slate-200 transition-colors"
                  data-testid={`quick-${opt.label.replace(/\s/g, '-')}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Date/Time */}
          <div>
            <Label className="text-slate-600 text-sm flex items-center gap-1 mb-2">
              <CalendarDays className="w-3.5 h-3.5" /> Date & Time (Eastern Time)
            </Label>
            <div className="flex gap-2">
              <Input
                type="date"
                value={reminderDate}
                onChange={(e) => setReminderDate(e.target.value)}
                className="flex-1 bg-white border-slate-300 text-black"
                data-testid="reminder-date"
              />
              <Input
                type="time"
                value={reminderTime}
                onChange={(e) => setReminderTime(e.target.value)}
                className="w-32 bg-white border-slate-300 text-black"
                data-testid="reminder-time"
              />
            </div>
          </div>

          {/* Recurring */}
          <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <Checkbox
              id="recurring"
              checked={recurringWeekly}
              onCheckedChange={setRecurringWeekly}
              data-testid="recurring-checkbox"
            />
            <label htmlFor="recurring" className="text-sm text-slate-700 flex items-center gap-1.5 cursor-pointer">
              <Repeat className="w-3.5 h-3.5 text-orange-500" />
              Repeat every week
            </label>
          </div>

          {/* Recipients */}
          <div>
            <Label className="text-slate-600 text-sm flex items-center gap-1 mb-2">
              <Users className="w-3.5 h-3.5" /> Remind Who?
            </Label>
            <div className="flex items-center gap-2 mb-2 p-2 bg-slate-50 rounded-lg">
              <Checkbox
                id="select-all"
                checked={selectAll}
                onCheckedChange={handleSelectAll}
              />
              <label htmlFor="select-all" className="text-sm text-slate-600 cursor-pointer">Select All</label>
            </div>
            <div className="max-h-40 overflow-y-auto space-y-1 rounded-lg border border-slate-200 p-2 bg-slate-50">
              {teamMembers.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-100 cursor-pointer"
                  onClick={() => toggleMember(member.id)}
                  data-testid={`member-${member.id}`}
                >
                  <Checkbox
                    checked={selectedMembers.includes(member.id)}
                    onCheckedChange={() => toggleMember(member.id)}
                  />
                  <span className="text-sm text-slate-900">{member.name}</span>
                  <span className="text-xs text-slate-400 ml-auto">{member.role}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            disabled={loading || selectedMembers.length === 0}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold"
            data-testid="set-reminder-btn"
          >
            {loading ? 'Setting...' : recurringWeekly ? 'Set Weekly Reminder' : 'Set Reminder'}
          </Button>

          {/* Existing Reminders */}
          {existingReminders.length > 0 && (
            <div>
              <Label className="text-slate-500 text-xs mb-2 block">Active Reminders</Label>
              <div className="space-y-1">
                {existingReminders.map((r) => (
                  <div key={r.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg border border-slate-200 text-xs">
                    <div>
                      <span className="text-slate-700">
                        {new Date(r.remind_at).toLocaleString('en-CA', { timeZone: 'America/Toronto', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {r.recurring_weekly && <span className="text-orange-500 ml-2">Weekly</span>}
                      <span className="text-slate-400 ml-2">{r.recipient_ids.length} recipient(s)</span>
                    </div>
                    <button
                      onClick={() => handleDeleteReminder(r.id)}
                      className="text-red-400 hover:text-red-300 p-1"
                      data-testid={`delete-reminder-${r.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
